import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider
} from "@mui/material";
import {
  FileText,
  Clock,
  TrendingUp,
  MessageSquare,
  Activity,
  CheckCircle2,
  AlertCircle,
  ShieldAlert
} from "lucide-react";
import moment from "moment";
import axios from "axios";
import { getBackendUrl } from "../../config";

const PublicReport = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);

  useEffect(() => {
    // Configura a meta tag robots noindex
    let metaTag = document.querySelector("meta[name='robots']");
    if (!metaTag) {
      metaTag = document.createElement("meta");
      metaTag.name = "robots";
      document.head.appendChild(metaTag);
    }
    metaTag.content = "noindex, nofollow";

    return () => {
      if (metaTag) {
        metaTag.content = "all";
      }
    };
  }, []);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        const backendUrl = getBackendUrl();
        const { data } = await axios.get(`${backendUrl}/public/reports/${token}`);
        setReport(data);
      } catch (err) {
        setError("Este relatório está indisponível ou expirou.");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchReport();
    } else {
      setError("Este relatório está indisponível ou expirou.");
      setLoading(false);
    }
  }, [token]);

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f8fafc" }}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  if (error || !report) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f8fafc", p: 3 }}>
        <Paper elevation={0} sx={{ p: 4, maxWidth: 480, textAlign: "center", borderRadius: "16px", border: "1px solid #e2e8f0" }}>
          <Box sx={{ display: "inline-flex", p: 2, bgcolor: "#fee2e2", color: "#dc2626", borderRadius: "50%", mb: 2 }}>
            <ShieldAlert size={36} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: "#1e293b" }}>
            Link Indisponível
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Este relatório não está acessível, foi revogado pelo administrador ou expirou sua validade.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const totals = report.metrics?.totals || {};
  const attendants = report.metrics?.attendants || [];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", py: { xs: 3, md: 5 } }}>
      <Container maxWidth="lg">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 4 },
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            bgcolor: "#ffffff"
          }}
        >
          {/* Cabeçalho Público (Seção 6.3) */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2, mb: 3 }}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  Relatório Consolidado de Atendimento
                </Typography>
                <Chip size="small" label="Público" color="primary" variant="outlined" />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Empresa: <strong>{report.company_name}</strong> (ID: {report.company_id}) • Período: <strong>{moment(report.period_start).format("DD/MM/YYYY")}</strong> até <strong>{moment(report.period_end).format("DD/MM/YYYY")}</strong>
              </Typography>
            </Box>

            <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
              <Chip
                label={
                  report.scope === "attendant"
                    ? "Por Atendente"
                    : report.scope === "connection"
                    ? `Por Conexão (${report.whatsapp_name || "Conexão"})`
                    : "Geral da Empresa"
                }
                size="small"
                sx={{ fontWeight: 600, bgcolor: "#e0f2fe", color: "#0369a1", mb: 0.5 }}
              />
              <Typography variant="caption" display="block" color="text.secondary">
                Gerado em: {moment(report.created_at).format("DD/MM/YYYY HH:mm:ss")}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Cards de Métricas Agregadas (D2) */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", mb: 2 }}>
            Indicadores Consolidados
          </Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: "12px", border: "1px solid #e2e8f0", bgcolor: "#f8fafc" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#2563eb", mb: 1 }}>
                  <MessageSquare size={18} />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>TOTAL CRIADOS</Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  {totals.created?.toLocaleString("pt-BR") ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {totals.createdTrend ? `${totals.createdTrend}% vs período anterior` : "—"}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: "12px", border: "1px solid #e2e8f0", bgcolor: "#f8fafc" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#059669", mb: 1 }}>
                  <CheckCircle2 size={18} />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>TOTAL FECHADOS</Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  {totals.closed?.toLocaleString("pt-BR") ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {totals.closedTrend ? `${totals.closedTrend}% vs período anterior` : "—"}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: "12px", border: "1px solid #e2e8f0", bgcolor: "#f8fafc" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#d97706", mb: 1 }}>
                  <Clock size={18} />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>ESPERA MÉDIA (TME)</Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  {totals.avgWaitMinutes !== null ? `${totals.avgWaitMinutes} min` : "—"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {totals.avgWaitTrend ? `${totals.avgWaitTrend}% vs período anterior` : "—"}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: "12px", border: "1px solid #e2e8f0", bgcolor: "#f8fafc" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#0284c7", mb: 1 }}>
                  <TrendingUp size={18} />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>CONVERSÃO</Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  {totals.conversionRate !== null ? `${totals.conversionRate}%` : "—"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {totals.closureRate ? `${totals.closureRate}% taxa fechamento` : "—"}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Tabela de Atendentes sem dados de contato (D2 e D7) */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", mb: 2 }}>
            Desempenho da Equipe (Métricas por Atendente)
          </Typography>
          <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "12px", mb: 4 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: "#f1f5f9" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Atendente</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Criados</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Fechados</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>TME</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>TMA</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Conversão</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Status Amostra (D7)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attendants.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3, color: "text.secondary" }}>
                      Nenhum atendente registrado no período.
                    </TableCell>
                  </TableRow>
                ) : (
                  attendants.map((att, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{att.userName}</TableCell>
                      <TableCell align="center">{att.createdTickets}</TableCell>
                      <TableCell align="center">{att.closedTickets}</TableCell>
                      <TableCell align="center">{att.avgWaitMinutes !== null ? `${att.avgWaitMinutes}m` : "—"}</TableCell>
                      <TableCell align="center">{att.avgServiceMinutes !== null ? `${att.avgServiceMinutes}m` : "—"}</TableCell>
                      <TableCell align="center">{att.conversionRate !== null ? `${att.conversionRate}%` : "—"}</TableCell>
                      <TableCell align="center">
                        {att.hasSufficientSample ? (
                          <Chip size="small" label="Suficiente (≥10)" color="success" variant="outlined" sx={{ fontSize: "0.75rem" }} />
                        ) : (
                          <Chip size="small" label="Amostra insuficiente" color="warning" variant="outlined" sx={{ fontSize: "0.75rem" }} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Análise Estruturada do Agente de IA */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", mb: 2 }}>
            Parecer Diagnóstico do Agente de IA
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              bgcolor: "#f8fafc",
              lineHeight: 1.8,
              whiteSpace: "pre-line",
              fontSize: "0.95rem",
              color: "#334155"
            }}
          >
            {report.analysis || "Análise do Agente de IA em processamento."}
          </Paper>

          {/* Rodapé Seguro (Seção 6.3) */}
          <Box sx={{ mt: 4, pt: 2, borderTop: "1px solid #e2e8f0", textAlign: "center" }}>
            <Typography variant="caption" color="text.secondary">
              Gerado em {moment(report.created_at).format("DD/MM/YYYY HH:mm")}. Link válido até {moment(report.expires_at).format("DD/MM/YYYY")}.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PublicReport;
